import React, { useContext, useEffect, useState } from "react";
import { History } from "history";
import { sendConfirmationEmail } from "../../../context/auth/action";
import { IToastContext, ToastContext } from "../../../providers/Toast";
import Button from "../../../components/shared/button/Button";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../routing/routes";
import queryString from "query-string";
import firebase from "./../../../firebase/firebase";
import * as Yup from "yup";
import { Link, useHistory } from "react-router-dom";
import CustomInputEmail from "../../../components/shared/forms/input/CustomInputEmail";
import { useFormik } from "formik";
import _environment from "../../../_environment/_environment";
import { PAGES } from "../../../routing/pages";

declare const $: any;

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {}

const ConfirmEmailView = ({ ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Access to the history instance that you may use to navigate.
  let history = useHistory();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------

  const [requesting, setRequesting] = useState<boolean>(false);
  const [requested, setRequested] = useState<boolean>(false);

  const [verifying, setVerifying] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Handle email verification from firebase
   * @function
   * @async
   *
   * @param actionCode
   */
  const handleVerifyEmail = async (actionCode: any) => {
    setVerifying(true);
    try {
      // Try to apply the email verification code.
      await firebase.auth().applyActionCode(actionCode);
      setVerified(true);
    } catch (err) {
      console.error(err);
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.mail_verification",
        callback: () => {},
      });
    } finally {
      setVerifying(false);
    }
  };

  /**
   * Request email confirmation
   * @function
   * @async
   */
  const requestEmailSend = async (email: string) => {
    setRequesting(true);

    try {
      const res = await sendConfirmationEmail(email);
      setRequested(true);

      openToast({
        type: res.success ? "success" : "error",
        title: res.success ? "success.general.title" : "errors.general.title",
        message: res.success ? "success.email_sent" : "errors.email_sent",
        callback: () => {},
      });
    } catch (err) {
      openToast({
        type: "error",
        title: "errors.general.title",
        message: "errors.email_sent",
        callback: () => {},
      });
    } finally {
      setRequesting(false);
    }
  };

  const handleSubmit = (data: any) => {
    if (!requested && !requesting) {
      return formik.handleSubmit(data);
    }
  };

  // -----------------------------------------------------------------
  // f o r m
  // -----------------------------------------------------------------
  // Formik Initialisation data
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email("Invalid email address").required("Email is required!"),
    }),
    onSubmit: (values: any) => {
      requestEmailSend(values.email);
    },
  });

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  const renderForm = (): React.ReactNode => {
    return (
      <>
        <Link className="btn btn-primary btn-block" id="back-to-sign-in" to={ROUTES.SIGN_IN}>
          {t("forms.back_sign_in")}
        </Link>

        <form className="user ask-for-email hidden">
          <div className="form-group">
            <CustomInputEmail
              id="email"
              name="email"
              label={t("forms.email")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              errors={formik.errors.email}
              touched={formik.touched.email}
            />
          </div>
          <Button
            loading={requesting}
            disabled={requesting}
            onClick={handleSubmit}
            submit={true}
            className="btn btn-primary btn-block"
          >
            {t("forms.submit")}
          </Button>
        </form>
      </>
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    console.log("[ConfirmEmailView] init");

    // If params, is verifying email
    const params = queryString.parse(history.location.search);
    if (params.mode === "verifyEmail" && !!params.oobCode) {
      handleVerifyEmail(params.oobCode);
    }

    // jQuery
    $("#back-to-sign-in-link").hide();

    $("#ask-for-email").on("click", () => {
      $("#ask-for-email").hide();
      $("#back-to-sign-in").hide();
      $("#back-to-sign-in-link").show();
      $(".ask-for-email").removeClass("hidden").fadeIn();
    });
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    console.log("[ConfirmEmailView] destroy");
    // destroy all
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="main-content">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-7 col-md-9">
            <div className="box o-hidden my-5">
              <div className="logo-vertical-wrapper">
                <a href={_environment.baseURL}>
                  <img src={_environment.assets.logo} alt={t("title")} />
                </a>
              </div>
              <div className="card-body p-0">
                <div className="p-4">
                  {/* If not verifying */}
                  {!verified && !verifying && (
                    <>
                      <div className="text-center">
                        <h1 className="h4 text-gray-900 mb-4">{t("pages.confirmEmail.title")}</h1>
                      </div>
                      <p className="description mb-4">{t("pages.confirmEmail.description")}</p>
                      {renderForm()}
                      <hr />
                      <div className="text-center">
                        <a className="link-label" id="ask-for-email">
                          {t("forms.email_not_received")}
                        </a>

                        <Link
                          className="link-label"
                          id="back-to-sign-in-link"
                          to={ROUTES.SIGN_IN}
                        >
                          {t("forms.back_sign_in")}
                        </Link>
                      </div>
                    </>
                  )}

                  {/* Is is verifying */}
                  {!verified && verifying && (
                    <>
                      <div className="text-center">
                        <h1 className="h4 text-gray-900 mb-4">We are verifying your email...</h1>
                      </div>
                      <p className="description mb-4 text-center">
                        <i className="fas fa-spinner fa-spin"></i>
                      </p>
                    </>
                  )}

                  {/* If verified */}
                  {verified && !verifying && (
                    <>
                      <div className="text-center">
                        <h1 className="h4 text-gray-900 mb-4">
                          Congrats! Your email has been verified! You can return to sign in now!
                        </h1>
                      </div>
                      <a href={ROUTES.SIGN_IN} className="btn btn-primary btn-block">
                        {t("forms.back_sign_in")}
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ConfirmEmailView;
